/**
 * Deterministic state construction for tutorials, scripted lessons and tests.
 *
 * The engine owns this so that no other layer has to hand-build `GameState`.
 * A scenario is only a *starting position*: once built, play continues through
 * `getLegalActions` / `applyAction` like any other game.
 */

import { createRng, shuffle } from "./rng.js";
import { getRuleSet } from "./ruleset.js";
import { COPIES_PER_KIND, TILE_KINDS, sortTiles } from "./tiles.js";
import type { Tile, TileKind } from "./tiles.js";
import type { GamePhase, GameState, Meld, MeldKind, PlayerId, PlayerState, SeatWind } from "./types.js";
import { PLAYER_IDS, SEAT_WINDS } from "./types.js";

export interface ScenarioMeldSpec {
  kind: MeldKind;
  tiles: TileKind[];
  concealed?: boolean;
  claimedFrom?: PlayerId | null;
}

export interface ScenarioPlayerSpec {
  hand?: TileKind[];
  drawn?: TileKind | null;
  discards?: TileKind[];
  melds?: ScenarioMeldSpec[];
}

export interface ScenarioSpec {
  ruleSetId?: string;
  seed?: string;
  dealer?: PlayerId;
  turn?: PlayerId;
  phase?: Exclude<GamePhase, "ended" | "awaiting-claims">;
  /** Index 0..3. Missing players are dealt from what is left. */
  players?: ScenarioPlayerSpec[];
  /** Tiles at the front of the live wall, in draw order. */
  wall?: TileKind[];
  /** Live wall length. Defaults to whatever is left after the hands. */
  wallSize?: number;
}

class TilePool {
  private readonly available = new Map<TileKind, Tile[]>();

  constructor(seed: string) {
    const byKind = new Map<TileKind, Tile[]>();
    let id = 0;
    for (const kind of TILE_KINDS) {
      const tiles: Tile[] = [];
      for (let copy = 0; copy < COPIES_PER_KIND; copy += 1) {
        tiles.push({ id, kind });
        id += 1;
      }
      byKind.set(kind, tiles);
    }
    this.available = byKind;
    this.seed = seed;
  }

  private readonly seed: string;

  take(kind: TileKind): Tile {
    const tiles = this.available.get(kind);
    const tile = tiles?.shift();
    if (!tile) {
      throw new Error(`Scenario uses more than ${COPIES_PER_KIND} copies of ${kind}`);
    }
    return tile;
  }

  takeMany(kinds: readonly TileKind[]): Tile[] {
    return kinds.map((kind) => this.take(kind));
  }

  /** Everything not claimed by the scenario, shuffled by the scenario seed. */
  rest(): Tile[] {
    const remaining: Tile[] = [];
    for (const tiles of this.available.values()) remaining.push(...tiles);
    return shuffle(remaining, createRng(`${this.seed}:rest`));
  }
}

function meldFromSpec(spec: ScenarioMeldSpec, pool: TilePool): Meld {
  const tiles = sortTiles(pool.takeMany(spec.tiles));
  const concealed = spec.concealed ?? false;
  return {
    kind: spec.kind,
    tiles,
    concealed,
    claimedFrom: concealed ? null : (spec.claimedFrom ?? null),
    ...(spec.kind === "kong" ? { kongKind: concealed ? ("concealed" as const) : ("claimed" as const) } : {}),
  };
}

/**
 * Build a starting position from explicit hands. Throws when the tiles asked
 * for do not exist in a 136-tile set, so a broken lesson fails loudly.
 */
export function createScenario(spec: ScenarioSpec = {}): GameState {
  const ruleSet = getRuleSet(spec.ruleSetId);
  const seed = spec.seed ?? "scenario";
  const dealer: PlayerId = spec.dealer ?? 0;
  const turn: PlayerId = spec.turn ?? dealer;
  const pool = new TilePool(seed);

  const players = PLAYER_IDS.map((playerId) => {
    const playerSpec = spec.players?.[playerId] ?? {};
    const melds = (playerSpec.melds ?? []).map((meld) => meldFromSpec(meld, pool));
    const hand = sortTiles(pool.takeMany(playerSpec.hand ?? []));
    const drawn = playerSpec.drawn ? pool.take(playerSpec.drawn) : null;
    const discards = pool.takeMany(playerSpec.discards ?? []);
    const player: PlayerState = {
      id: playerId,
      seat: SEAT_WINDS[(playerId - dealer + 4) % 4] as SeatWind,
      hand,
      drawnTile: drawn,
      discards,
      melds,
    };
    return player;
  }) as GameState["players"];

  const scriptedWall = pool.takeMany(spec.wall ?? []);
  const leftovers = pool.rest();

  // Fill each player up to the size their meld count implies.
  for (const player of players) {
    const target = ruleSet.handSize - 3 * player.melds.length;
    while (player.hand.length < target) {
      const tile = leftovers.shift();
      if (!tile) break;
      player.hand.push(tile);
    }
    player.hand = sortTiles(player.hand);
  }

  const deadWall = leftovers.splice(0, ruleSet.deadWallSize);
  const wall = [...scriptedWall, ...leftovers];
  const limited = spec.wallSize === undefined ? wall : wall.slice(0, spec.wallSize);

  return {
    version: 2,
    ruleSetId: ruleSet.id,
    seed,
    dealer,
    turn,
    phase: spec.phase ?? (players[turn].drawnTile ? "awaiting-discard" : "awaiting-draw"),
    wall: limited,
    deadWall,
    players,
    lastDiscard: null,
    pendingClaims: null,
    actionCount: 0,
    result: null,
  };
}
